import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import { CheckCircle, Copy, Check, Download, Key, Mail, ArrowRight } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export default function PurchaseSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [purchaseData, setPurchaseData] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setError('No session ID found. Please check your email for your license key.');
      setLoading(false);
      return;
    }

    // Verify the session and get purchase details
    verifySession();
  }, [sessionId]);

  const verifySession = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch purchase details including license key
      const response = await axios.get(`${API_BASE_URL}/api/checkout/purchase/${sessionId}`);

      setPurchaseData(response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to verify purchase. Please check your email.';
      setError(errorMessage);

      // If webhook is still processing, retry after a delay
      if (errorMessage.includes('webhook') || errorMessage.includes('processing')) {
        setTimeout(() => {
          verifySession();
        }, 3000); // Retry after 3 seconds
      }
    } finally {
      setLoading(false);
    }
  };

  const copyLicenseKey = (key) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center pt-20">
        <div className="max-w-2xl w-full mx-auto px-4">
          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardHeader>
              <Skeleton className="h-8 w-64 mb-2 bg-purple-800/20" />
              <Skeleton className="h-4 w-full bg-purple-800/20" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-32 w-full bg-purple-800/20" />
              <Skeleton className="h-12 w-full bg-purple-800/20" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center pt-20">
        <div className="max-w-2xl w-full mx-auto px-4">
          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="text-red-400">Purchase Verification</CardTitle>
              <CardDescription className="text-gray-400">There was an issue verifying your purchase</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant="destructive" className="bg-red-900/20 border-red-500/50">
                <AlertDescription className="text-red-300">{error}</AlertDescription>
              </Alert>
              <div className="space-y-2">
                <p className="text-sm text-gray-300">
                  Don't worry! Your purchase was likely successful. Please check your email for:
                </p>
                <ul className="list-disc list-inside text-sm text-gray-400 space-y-1">
                  <li>Order confirmation from Stripe</li>
                  <li>License key delivery email from Cogsmith</li>
                </ul>
              </div>
              <div className="flex gap-3">
                <Button asChild variant="outline" className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10">
                  <Link to="/login">Go to Login</Link>
                </Button>
                <Button asChild className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                  <Link to="/dashboard">Go to Dashboard</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 pt-20">
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 mb-4 border border-green-500/30">
            <CheckCircle className="h-12 w-12 text-green-400" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">
            Purchase Successful!
          </h1>
          <p className="text-xl text-gray-300">
            Thank you for purchasing Cogsmith Pro
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* License Key Card */}
          <Card className="lg:col-span-2 bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Key className="h-5 w-5 text-purple-400" />
                <CardTitle className="text-white">Your License Key</CardTitle>
              </div>
              <CardDescription className="text-gray-400">
                Copy and save your license key - you'll need it to activate Cogsmith Pro
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {purchaseData?.license?.license_key ? (
                <>
                  <div className="relative">
                    <div className="bg-gray-800/50 border-2 border-purple-500/30 rounded-lg p-4 font-mono text-sm break-all text-gray-200">
                      {purchaseData.license.license_key}
                    </div>
                    <Button
                      onClick={() => copyLicenseKey(purchaseData.license.license_key)}
                      className="absolute top-2 right-2 bg-purple-600 hover:bg-purple-700"
                      size="sm"
                    >
                      {copiedKey ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>

                  <Alert className="bg-blue-900/20 border-blue-500/30">
                    <Mail className="h-4 w-4 text-blue-400" />
                    <AlertDescription className="text-blue-200">
                      A confirmation email with your license key has been sent to{' '}
                      <strong className="text-blue-100">{purchaseData.session?.customer_email || purchaseData.license?.email}</strong>
                    </AlertDescription>
                  </Alert>
                </>
              ) : (
                <Alert className="bg-blue-900/20 border-blue-500/30">
                  <Mail className="h-4 w-4 text-blue-400" />
                  <AlertDescription className="text-blue-200">
                    Your license key will be sent to your email address shortly.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* License Information */}
          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardHeader>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <CardTitle className="text-white">What's Included</CardTitle>
              </div>
              <CardDescription className="text-gray-400">
                Your Pro license features
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
                    All 132 premium tools unlocked
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
                    Lifetime license (no recurring fees)
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
                    Up to {purchaseData?.license?.device_limit || 3} device activations
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
                    Priority email support
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
                    All future updates included
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Next Steps */}
          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardHeader>
              <div className="flex items-center gap-2">
                <ArrowRight className="h-5 w-5 text-purple-400" />
                <CardTitle className="text-white">Next Steps</CardTitle>
              </div>
              <CardDescription className="text-gray-400">
                Get started with Cogsmith Pro
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30 flex items-center justify-center text-sm font-semibold">
                    1
                  </div>
                  <div>
                    <p className="font-medium text-white">Download Cogsmith</p>
                    <p className="text-sm text-gray-400">
                      Get the latest version for Windows
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30 flex items-center justify-center text-sm font-semibold">
                    2
                  </div>
                  <div>
                    <p className="font-medium text-white">Install and Launch</p>
                    <p className="text-sm text-gray-400">
                      Run the installer and open Cogsmith
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30 flex items-center justify-center text-sm font-semibold">
                    3
                  </div>
                  <div>
                    <p className="font-medium text-white">Activate Your License</p>
                    <p className="text-sm text-gray-400">
                      Enter your license key from the email
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30 flex items-center justify-center text-sm font-semibold">
                    4
                  </div>
                  <div>
                    <p className="font-medium text-white">Start Automating</p>
                    <p className="text-sm text-gray-400">
                      Explore all Pro features and tools
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
            <Link to="/download">
              <Download className="mr-2 h-5 w-5" />
              Download Cogsmith
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10">
            <Link to="/dashboard">
              View Dashboard
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10">
            <Link to="/docs">
              Read Documentation
            </Link>
          </Button>
        </div>

        {/* Support Section */}
        <Card className="mt-8 bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
          <CardContent className="py-6">
            <div className="text-center space-y-2">
              <p className="text-sm font-medium text-white">
                Need help getting started?
              </p>
              <p className="text-sm text-gray-300">
                Check out our{' '}
                <Link to="/docs" className="text-purple-400 hover:text-purple-300 font-medium">
                  documentation
                </Link>
                {' '}or contact us at{' '}
                <a href="mailto:info@cogsmith.ai" className="text-purple-400 hover:text-purple-300 font-medium">
                  info@cogsmith.ai
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

